const ALGORITHM = "AES-GCM";
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const ENVELOPE_MAGIC = new Uint8Array([0x41, 0x52, 0x44, 0x01]);

export type EncryptedOfflineBytes = {
  version: 1;
  iv: ArrayBuffer;
  ciphertext: ArrayBuffer;
};

export interface OfflineCryptoKeyProvider {
  getOrCreate(profileId: string): Promise<CryptoKey>;
  delete(profileId: string): Promise<void>;
}

function cryptoApi() {
  const value = globalThis.crypto;
  if (!value?.subtle || !value.getRandomValues) {
    throw new Error("Le chiffrement local sécurisé n’est pas disponible sur cet appareil.");
  }
  return value;
}

function bytes(value: ArrayBuffer | ArrayBufferView) {
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
}

function exactBuffer(value: ArrayBuffer | ArrayBufferView) {
  const source = bytes(value);
  return source.slice().buffer;
}

function additionalData(context: string) {
  if (!context.trim()) throw new Error("Le contexte du chiffrement local est manquant.");
  return new TextEncoder().encode(`ard-manager-offline:v1:${context}`).buffer;
}

export async function generateOfflineAesKey() {
  return cryptoApi().subtle.generateKey(
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ["encrypt", "decrypt"],
  );
}

export function generateOfflineRawKey() {
  const value = new Uint8Array(KEY_LENGTH / 8);
  cryptoApi().getRandomValues(value);
  return value;
}

export async function importOfflineAesKey(raw: ArrayBuffer | ArrayBufferView) {
  if (bytes(raw).byteLength !== KEY_LENGTH / 8) {
    throw new Error("La clé locale chiffrée est invalide.");
  }
  return cryptoApi().subtle.importKey(
    "raw",
    exactBuffer(raw),
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function encryptOfflineBytes(
  key: CryptoKey,
  context: string,
  value: ArrayBuffer | ArrayBufferView,
): Promise<EncryptedOfflineBytes> {
  const iv = new Uint8Array(IV_LENGTH);
  cryptoApi().getRandomValues(iv);
  const ciphertext = await cryptoApi().subtle.encrypt(
    { name: ALGORITHM, iv: iv.buffer, additionalData: additionalData(context) },
    key,
    exactBuffer(value),
  );
  return { version: 1, iv: iv.buffer, ciphertext };
}

export async function decryptOfflineBytes(
  key: CryptoKey,
  context: string,
  value: EncryptedOfflineBytes,
) {
  if (value.version !== 1 || bytes(value.iv).byteLength !== IV_LENGTH) {
    throw new Error("Le contenu local chiffré est invalide.");
  }
  try {
    return await cryptoApi().subtle.decrypt(
      {
        name: ALGORITHM,
        iv: exactBuffer(value.iv),
        additionalData: additionalData(context),
      },
      key,
      exactBuffer(value.ciphertext),
    );
  } catch {
    throw new Error("Le contenu local ne peut pas être déchiffré sur cet appareil.");
  }
}

export class OfflineLocalCipher {
  private readonly keys: OfflineCryptoKeyProvider;

  constructor(keys: OfflineCryptoKeyProvider) {
    this.keys = keys;
  }

  async encryptJson(profileId: string, context: string, value: unknown) {
    const key = await this.keys.getOrCreate(profileId);
    return encryptOfflineBytes(
      key,
      context,
      new TextEncoder().encode(JSON.stringify(value)),
    );
  }

  async decryptJson<T>(profileId: string, context: string, value: EncryptedOfflineBytes) {
    const key = await this.keys.getOrCreate(profileId);
    const plaintext = await decryptOfflineBytes(key, context, value);
    return JSON.parse(new TextDecoder().decode(plaintext)) as T;
  }

  async encryptBlob(profileId: string, context: string, value: Blob) {
    const key = await this.keys.getOrCreate(profileId);
    return encryptOfflineBytes(key, context, await value.arrayBuffer());
  }

  async decryptBlob(
    profileId: string,
    context: string,
    value: EncryptedOfflineBytes,
    mimeType: string,
  ) {
    const key = await this.keys.getOrCreate(profileId);
    return new Blob([await decryptOfflineBytes(key, context, value)], { type: mimeType });
  }

  purgeProfile(profileId: string) {
    return this.keys.delete(profileId);
  }
}

export class MemoryOfflineCryptoKeyProvider implements OfflineCryptoKeyProvider {
  private readonly keys = new Map<string, CryptoKey>();

  async getOrCreate(profileId: string) {
    const existing = this.keys.get(profileId);
    if (existing) return existing;
    const key = await generateOfflineAesKey();
    this.keys.set(profileId, key);
    return key;
  }

  async delete(profileId: string) {
    this.keys.delete(profileId);
  }
}

type IndexedDbKeyRow = { profileId: string; key: CryptoKey };

export class IndexedDbOfflineCryptoKeyProvider implements OfflineCryptoKeyProvider {
  private databasePromise: Promise<IDBDatabase> | null = null;

  private openDatabase() {
    if (!this.databasePromise) {
      this.databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open("ard-manager-offline-keys", 1);
        request.onupgradeneeded = () => {
          if (!request.result.objectStoreNames.contains("keys")) {
            request.result.createObjectStore("keys", { keyPath: "profileId" });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("Trousseau local indisponible."));
      });
    }
    return this.databasePromise;
  }

  async getOrCreate(profileId: string) {
    const database = await this.openDatabase();
    const existing = await new Promise<IndexedDbKeyRow | undefined>((resolve, reject) => {
      const request = database.transaction("keys", "readonly").objectStore("keys").get(profileId);
      request.onsuccess = () => resolve(request.result as IndexedDbKeyRow | undefined);
      request.onerror = () => reject(request.error ?? new Error("Lecture de la clé locale impossible."));
    });
    if (existing?.key) return existing.key;

    const key = await generateOfflineAesKey();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction("keys", "readwrite");
      transaction.objectStore("keys").put({ profileId, key } satisfies IndexedDbKeyRow);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Écriture de la clé locale impossible."));
      transaction.onabort = () => reject(transaction.error ?? new Error("Écriture de la clé locale annulée."));
    });
    return key;
  }

  async delete(profileId: string) {
    const database = await this.openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction("keys", "readwrite");
      transaction.objectStore("keys").delete(profileId);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Suppression de la clé locale impossible."));
      transaction.onabort = () => reject(transaction.error ?? new Error("Suppression de la clé locale annulée."));
    });
  }
}

export function packEncryptedOfflineBytes(value: EncryptedOfflineBytes) {
  if (value.version !== 1 || bytes(value.iv).byteLength !== IV_LENGTH) {
    throw new Error("Le contenu local chiffré est invalide.");
  }
  const iv = bytes(value.iv);
  const ciphertext = bytes(value.ciphertext);
  const packed = new Uint8Array(ENVELOPE_MAGIC.byteLength + iv.byteLength + ciphertext.byteLength);
  packed.set(ENVELOPE_MAGIC, 0);
  packed.set(iv, ENVELOPE_MAGIC.byteLength);
  packed.set(ciphertext, ENVELOPE_MAGIC.byteLength + iv.byteLength);
  return packed;
}

export function unpackEncryptedOfflineBytes(value: ArrayBuffer | ArrayBufferView) {
  const packed = bytes(value);
  const minimumLength = ENVELOPE_MAGIC.byteLength + IV_LENGTH + 16;
  if (
    packed.byteLength < minimumLength ||
    !ENVELOPE_MAGIC.every((byte, index) => packed[index] === byte)
  ) {
    throw new Error("Le fichier local chiffré est invalide.");
  }
  return {
    version: 1,
    iv: packed.slice(ENVELOPE_MAGIC.byteLength, ENVELOPE_MAGIC.byteLength + IV_LENGTH).buffer,
    ciphertext: packed.slice(ENVELOPE_MAGIC.byteLength + IV_LENGTH).buffer,
  } satisfies EncryptedOfflineBytes;
}
